import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import {
  closeCompanyTab,
  loadCompanyOpenTabs,
} from "./useCompanyOpenTabs";
import { useSim } from "./SimContext";

export default function CompanyTabsBar({ activeTabId }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { removeSimulatedEntry } = useSim();
  const [openTabs, setOpenTabs] = useState(() => loadCompanyOpenTabs());

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key && e.key !== "company-open-tabs-v1") return;
      setOpenTabs(loadCompanyOpenTabs());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    setOpenTabs(loadCompanyOpenTabs());
  }, [location.pathname, location.search]);

  const tabs = useMemo(() => openTabs || [], [openTabs]);

  const simTabFromUrl = useMemo(() => {
    const q = new URLSearchParams(location.search).get("simTab");
    return q && String(q).trim() ? String(q).trim() : null;
  }, [location.search]);

  const resolvedActiveTabId = simTabFromUrl || activeTabId;

  const goCompanyRootIfActive = (tabId) => {
    if (String(tabId) !== String(resolvedActiveTabId)) return;
    const params = new URLSearchParams(location.search);
    params.delete("simTab");
    const q = params.toString();
    navigate(`/company${q ? `?${q}` : ""}`);
  };

  return (
    <div
      className="companyTabsBarSticky company__catalogueTabs companyTabsBar company-catalog-root"
      style={{
        borderBottom: "1px solid rgba(226, 232, 240, 0.85)",
        background: "#fff",
        boxShadow: "0 1px 0 rgba(15, 23, 42, 0.04)",
      }}
    >
      <div className="companyTabsBar__track" role="tablist" aria-label="Open companies">
        <Link
          to="/company"
          className={
            "companyTabsBar__catalogueLink" +
            (!resolvedActiveTabId ? " is-active" : "")
          }
          style={{
            color: !resolvedActiveTabId ? "rgb(32, 53, 99)" : "rgb(100, 116, 139)",
          }}
        >
          Company Catalogue
        </Link>
        {tabs.map((t) => {
          const active = String(t.id) === String(resolvedActiveTabId);
          const isSim = Boolean(t.sim);
          if (isSim) {
            return (
              <div
                key={t.id}
                className={
                  "companyTabsBar__tabRow companyTabsBar__tabRow--sim" +
                  (active ? " is-active" : "")
                }
              >
                <span
                  className="companyTabsBar__dot"
                  style={{
                    background: "#FFCC00",
                    boxShadow: "0 0 0 1px rgba(186,117,23,0.4)",
                  }}
                />
                <Link to={t.to || "/company"} style={{ color: "inherit", fontWeight: 600 }}>
                  {t.name || t.id}
                </Link>
                <span
                  style={{
                    fontSize: 9,
                    fontWeight: 600,
                    padding: "1px 6px",
                    borderRadius: 20,
                    background: "#FFCC00",
                    color: "#042C53",
                  }}
                >
                  SIM
                </span>
                <button
                  type="button"
                  className="companyTabsBar__tabClose"
                  onClick={() => {
                    removeSimulatedEntry(t.id);
                    setOpenTabs(loadCompanyOpenTabs());
                    goCompanyRootIfActive(t.id);
                  }}
                  aria-label={`Close ${t.name || t.id}`}
                >
                  <X width={14} height={14} />
                </button>
              </div>
            );
          }
          return (
            <div
              key={t.id}
              className={
                "companyTabsBar__tabRow companyTabsBar__tabRow--open" +
                (active ? " is-active" : "")
              }
            >
              <span className="companyTabsBar__dot" style={{ background: "#059669" }} />
              <Link
                to={t.to || "/company"}
                style={{
                  color: "inherit",
                  fontWeight: 600,
                  maxWidth: 200,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {t.name || t.id}
              </Link>
              <button
                type="button"
                className="companyTabsBar__tabClose"
                onClick={() => {
                  const next = closeCompanyTab(t.id);
                  setOpenTabs(next);
                  goCompanyRootIfActive(t.id);
                }}
                aria-label={`Close ${t.name || t.id}`}
              >
                <X width={14} height={14} />
              </button>
              {active ? (
                <span className="companyTabsBar__tabUnderline" style={{ background: "#059669" }} />
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
